import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDownIcon } from "@heroicons/react/outline";
import { IProjectLanguageData } from "src/model/model";
import { SearchInput } from "src/common/components/SearchInput";
import { useSelector } from "react-redux";
import { getAllLanguages, ILanguage } from "src/features/languages/state/languagesSlice";
import { usePrettyLanguageName } from "../../languages/util/prettyLanguageName";

interface LanguageSwitcherProps {
  plds: IProjectLanguageData[];
  activeLanguageId: number;
  sourceLanguageId: number;
  onChange: (languageId: number) => void;
  staleCountByLanguageId: Record<number, number>;
}

const SEARCH_THRESHOLD = 5;

const LanguageSwitcher = ({
  plds,
  activeLanguageId,
  sourceLanguageId,
  onChange,
  staleCountByLanguageId,
}: LanguageSwitcherProps) => {
  const { t } = useTranslation();
  const pretty = usePrettyLanguageName();
  const allLanguages = useSelector(getAllLanguages);
  const languagesById = useMemo(() => {
    const map = new Map<number, ILanguage>();
    for (const l of allLanguages) map.set(l.id, l);
    return map;
  }, [allLanguages]);

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (isOpen && containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  // Reset filter on close; auto-focus search on open.
  useEffect(() => {
    if (isOpen) {
      const id = window.setTimeout(() => searchInputRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
    setSearch("");
    return undefined;
  }, [isOpen]);

  const showSearch = plds.length >= SEARCH_THRESHOLD;

  const filteredPlds = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return plds;
    return plds.filter((pld) => {
      const langId = Number(pld.language);
      if (pretty(langId).toLowerCase().includes(q)) return true;
      const lang = languagesById.get(langId);
      if (lang?.IETF_BCP_47?.toLowerCase().includes(q)) return true;
      if (lang?.ISO_639_1?.toLowerCase().includes(q)) return true;
      return false;
    });
  }, [plds, search, pretty, languagesById]);

  const renderBadge = (langId: number) => {
    const isSource = Number(langId) === Number(sourceLanguageId);
    if (isSource) {
      return (
        <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide bg-gray-100 text-gray-500">
          {t("pages.project.editNode.sourceLanguageBadge")}
        </span>
      );
    }
    const count = staleCountByLanguageId[langId] ?? 0;
    if (count > 0) {
      return (
        <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-[20px] px-1 rounded-full bg-yellow-200 text-yellow-800 text-[10px] font-medium">
          {count}
        </span>
      );
    }
    return null;
  };

  const activeLabel = pretty(activeLanguageId);

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        type="button"
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-gray-300 bg-white text-sm hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-green-500"
        onClick={() => setIsOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="font-medium">{activeLabel}</span>
        {renderBadge(activeLanguageId)}
        <ChevronDownIcon
          className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>
      {isOpen && (
        <div className="absolute z-20 mt-1 left-0 min-w-[18rem] rounded-md border border-gray-200 bg-white shadow-lg">
          {showSearch && (
            <div className="p-2 border-b border-gray-100">
              <SearchInput
                ref={searchInputRef}
                value={search}
                onChange={setSearch}
                placeholder={t("general.languages.search") as string}
              />
            </div>
          )}
          <ul role="listbox" className="py-1 max-h-72 overflow-y-auto personalize-scroll-visible">
            {filteredPlds.length === 0 ? (
              <li className="px-3 py-2 text-sm text-gray-400 italic">{t("general.noResults")}</li>
            ) : (
              filteredPlds.map((pld) => {
                const langId = Number(pld.language);
                const isActive = langId === Number(activeLanguageId);
                return (
                  <li key={langId}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      className={`w-full text-left px-3 py-1.5 text-sm flex items-center justify-between gap-3 whitespace-nowrap hover:bg-green-50 ${
                        isActive ? "bg-green-100 font-medium" : ""
                      }`}
                      onClick={() => {
                        onChange(langId);
                        setIsOpen(false);
                      }}
                    >
                      <span>{pretty(langId)}</span>
                      {renderBadge(langId)}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
